/** @jsxImportSource preact */
import { useState } from 'preact/hooks'
import { config } from '../config'

interface Row {
  bookingId: string
  action: 'pending' | 'running' | 'would-close' | 'closed' | 'skipped' | 'error'
  reason: string
  refundStatus?: string | null
}

type Phase = 'idle' | 'previewing' | 'previewed' | 'closing' | 'done'

const BOOKING_ID_PATTERN = /^PLT\d+$/

/** How many closes are in flight at once. Each one is a separate request against its own booking. */
const CONCURRENCY = 5

/** A politeness limit on one screenful, not a timeout — no single request carries the whole list. */
const MAX_BOOKINGS = 50

/**
 * Splits a pasted list the way ops actually pastes one — a line each, a comma-separated run, or a
 * CSV column — and reports the unusable tokens instead of dropping them. A PNR pasted here would
 * otherwise reach the API and be refused one row at a time, which reads as a server problem.
 */
export function parseBookingIds(raw: string): { ids: string[]; rejected: string[] } {
  const seen = new Set<string>()
  const ids: string[] = []
  const rejected: string[] = []

  for (const token of raw.split(/[\s,"]+/)) {
    const value = token.trim().toUpperCase()
    if (!value) continue

    if (!BOOKING_ID_PATTERN.test(value)) {
      if (!rejected.includes(value)) rejected.push(value)
      continue
    }
    if (seen.has(value)) continue

    seen.add(value)
    ids.push(value)
  }

  return { ids, rejected }
}

/** Runs `worker` over the list a few at a time, keeping the pool full rather than waiting on batches. */
export async function forEachConcurrently<T>(items: T[], limit: number, worker: (item: T) => Promise<void>) {
  let next = 0
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      await worker(items[next++]!)
    }
  })
  await Promise.all(runners)
}

export function BulkRefundClose() {
  const [input, setInput] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState('')
  // Counted rather than derived from the table: closing runs over a subset of it, so a count of
  // settled rows would be measured against the wrong total.
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const { ids, rejected } = parseBookingIds(input)
  const closable = rows.filter(r => r.action === 'would-close')
  const running = phase === 'previewing' || phase === 'closing'
  const overCap = ids.length > MAX_BOOKINGS

  // Keyed by booking id, not position: closing keeps the previewed table so the rows it skipped
  // stay on screen next to the ones it acted on.
  const updateRow = (bookingId: string, row: Partial<Row>) =>
    setRows(current => current.map(r => (r.bookingId === bookingId ? { ...r, ...row } : r)))

  // One request per booking, so a booking whose workflow is slow to wake holds up nothing but its
  // own row, and a closed row stays closed even if the operator walks away mid-run.
  const run = async (dryRun: boolean) => {
    const targets = dryRun ? ids : closable.map(r => r.bookingId)
    if (targets.length === 0) {
      setError(dryRun ? 'Enter at least one booking id' : 'Nothing to close')
      return
    }

    setError('')
    setPhase(dryRun ? 'previewing' : 'closing')
    setProgress({ done: 0, total: targets.length })
    if (dryRun) {
      setRows(targets.map(bookingId => ({ bookingId, action: 'pending', reason: '' })))
    }

    await forEachConcurrently(targets, CONCURRENCY, async bookingId => {
      updateRow(bookingId, { action: 'running', reason: dryRun ? 'Reading…' : 'Closing…' })

      try {
        const resp = await fetch(`${config.apiBaseUrl}/bookings/${bookingId}/cancel-execution/force-close`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ dryRun }),
        })
        const body = await resp.json().catch(() => ({}))
        if (!resp.ok) throw new Error(body.error ?? `Request failed (${resp.status})`)

        updateRow(bookingId, { action: body.action, reason: body.reason, refundStatus: body.refundStatus })
      } catch (err) {
        updateRow(bookingId, { action: 'error', reason: err instanceof Error ? err.message : 'Unknown error' })
      } finally {
        setProgress(current => ({ ...current, done: current.done + 1 }))
      }
    })

    setPhase(dryRun ? 'previewed' : 'done')
  }

  const reset = () => {
    setInput('')
    setRows([])
    setPhase('idle')
    setError('')
    setProgress({ done: 0, total: 0 })
  }

  const badgeClass = (action: Row['action']) =>
    action === 'closed'
      ? 'bg-green-100 text-green-800'
      : action === 'would-close'
        ? 'bg-blue-100 text-blue-800'
        : action === 'error'
          ? 'bg-red-100 text-red-800'
          : action === 'pending' || action === 'running'
            ? 'bg-gray-100 text-gray-700'
            : 'bg-amber-100 text-amber-800'

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-1">Close refunds in bulk</h1>
      <p className="text-gray-600 mb-6">
        For cancellations that were never going to return money — a default or loss-recovery cancellation. Closing stops
        the Sabre poll, records the refund as not expected, and notes who closed it on each cancellation record.
      </p>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="bulkBookingIds"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Booking IDs <span className="text-red-500">*</span>
          </label>
          <textarea
            id="bulkBookingIds"
            rows={8}
            value={input}
            onChange={e => setInput((e.target as HTMLTextAreaElement).value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
            placeholder={'One booking id per line, or paste a CSV column — e.g.\nPLT2885644\nPLT2900554'}
          />
          <p className="mt-1 text-xs text-gray-500">
            {ids.length} booking id(s) recognised. Each is closed on its own, {CONCURRENCY} at a time — refunds already
            completed, or already closed as not expected, are left alone and listed below with why.
          </p>
          {overCap && (
            <p className="mt-1 text-xs text-red-600">
              {ids.length} is more than the {MAX_BOOKINGS} this runs in one go. Split the list.
            </p>
          )}
          {rejected.length > 0 && (
            <p className="mt-1 text-xs text-red-600">
              Not booking ids, and ignored: <span className="font-mono">{rejected.join(', ')}</span>. A PNR addresses no
              cancellation workflow — use the PLT booking id.
            </p>
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={() => run(true)}
            disabled={running || ids.length === 0 || overCap}
            className="px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {phase === 'previewing' ? `Reading ${progress.done} of ${progress.total}…` : 'Preview'}
          </button>
          <button
            type="button"
            onClick={() => run(false)}
            disabled={running || phase !== 'previewed' || closable.length === 0}
            title={phase !== 'previewed' ? 'Preview first' : undefined}
            className="px-4 py-2 bg-gray-700 text-white font-medium rounded-md shadow-sm hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {phase === 'closing'
              ? `Closing ${progress.done} of ${progress.total}…`
              : `Close ${closable.length} refund(s)`}
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={running}
            className="px-4 py-2 bg-white text-gray-700 font-medium border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Reset
          </button>
        </div>

        {rows.length > 0 && (
          <div className="mt-2 border border-gray-200 rounded-md overflow-hidden overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Booking</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Result</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Refund status</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map(row => (
                  <tr key={row.bookingId}>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{row.bookingId}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' + badgeClass(row.action)}>
                        {row.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-600 whitespace-nowrap">{row.refundStatus ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{row.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {phase === 'done' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-700">
              Closed {rows.filter(r => r.action === 'closed').length} refund(s). A workflow that was still polling can
              take a moment to settle — preview again if you need to confirm.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
